import { isNodeEmpty } from "@tiptap/react";

import { twMerge } from "@/utils/twMerge";

import type { Alignment } from "@/components/doc/right-sidebar/align-dropdown/AlignDropdown";
import type { Node as ProsemirrorNode } from "@tiptap/pm/model";
import type { PropsWithChildren } from "react";

type PlaceholderProps = {
  text: string;
  isVisibleUnfocused?: boolean;
  className?: string;
  alignment?: Alignment;
};

export function PlaceholderText({
  text,
  alignment,
  isVisibleUnfocused,
  className,
}: PlaceholderProps) {
  // contentEditable={false} is needed to make ArrowLeft work for the empty paragraph
  return (
    <span
      contentEditable={false}
      className={twMerge(
        "absolute top-0 left-0 w-full h-full overflow-hidden text-ellipsis pointer-events-none text-gray-400/50",
        alignment === "center" && "text-center",
        alignment === "right" && "text-right",
        !isVisibleUnfocused && "tiptap-node-placeholder-visibility",
        className,
      )}
      aria-hidden
    >
      <span
        contentEditable={false}
        className="whitespace-nowrap tiptap-node-placeholder"
        data-placeholder={text}
      />
    </span>
  );
}

type Props = PlaceholderProps & {
  node: ProsemirrorNode;
  alignment?: Alignment;
};

export function Placeholder({
  node,
  alignment,
  children,
  ...placeholderProps
}: PropsWithChildren<Props>) {
  const isEmpty = isNodeEmpty(node);

  return (
    <div className="relative w-full">
      {isEmpty && <PlaceholderText alignment={alignment} {...placeholderProps} />}
      {children}
    </div>
  );
}
